import { useEffect, useMemo, useState, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { StellarSelect } from './StellarFormControls';
import { ChevronRight, Network, AlertTriangle, CheckCircle2, Lock } from 'lucide-react';
import { toast } from 'sonner';
import { UserChip } from './UserChip';

/**
 * OrgChartCard — Settings -> Member Management -> Org Chart (Iter 52, Iteration 2)
 *
 * Renders a tree view of workspace members based on manager_id. Owner sits at top;
 * non-Owner members render under whoever they "report to". Each non-Owner row has
 * a "Reports to" dropdown (filtered by role hierarchy) when the current viewer is
 * Owner/Admin. Manager/Team see a read-only tree.
 *
 * Backend visibility logic falls back to iter-51 behavior until every non-owner
 * member has a non-null manager_id - we surface that status with a warning banner.
 */
export function OrgChartCard({ workspaceId, viewerRole }) {
  const canEdit = ['super_admin', 'owner', 'admin'].includes(viewerRole);
  const [data, setData] = useState({ members: [], hierarchy_complete: false, allowed_managers_by_role: {} });
  const [loading, setLoading] = useState(true);
  const [savingId, setSavingId] = useState(null);

  const fetchChart = useCallback(async () => {
    if (!workspaceId) return;
    setLoading(true);
    try {
      const r = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/workspaces/${workspaceId}/org-chart`,
        { headers: { Authorization: `Bearer ${localStorage.getItem('stellar_token')}` } }
      );
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      const body = await r.json();
      setData(body);
    } catch (e) {
      toast.error('Failed to load org chart');
    } finally {
      setLoading(false);
    }
  }, [workspaceId]);

  useEffect(() => { fetchChart(); }, [fetchChart]);

  const updateManager = async (memberId, newManagerId) => {
    setSavingId(memberId);
    setData(prev => ({
      ...prev,
      members: prev.members.map(m => m.member_id === memberId ? { ...m, manager_id: newManagerId } : m),
    }));
    try {
      const r = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/workspaces/${workspaceId}/members/${memberId}/manager`,
        {
          method: 'PUT',
          headers: { Authorization: `Bearer ${localStorage.getItem('stellar_token')}`, 'Content-Type': 'application/json' },
          body: JSON.stringify({ manager_id: newManagerId }),
        }
      );
      if (!r.ok) {
        const body = await r.json().catch(() => ({}));
        throw new Error(body.detail || `HTTP ${r.status}`);
      }
      toast.success('Reports-to updated');
      fetchChart();
    } catch (e) {
      toast.error(`Update failed: ${e.message || 'unknown'}`);
      fetchChart();
    } finally {
      setSavingId(null);
    }
  };

  // Flatten the tree into [{ member, depth }, ...] so we can render a flat list.
  const flatTree = useMemo(() => {
    const childrenByMgr = {};
    let owner = null;
    for (const m of data.members) {
      if (m.role === 'owner') { owner = m; continue; }
      const k = m.manager_id || '__none__';
      if (!childrenByMgr[k]) childrenByMgr[k] = [];
      childrenByMgr[k].push(m);
    }
    const order = { admin: 0, manager: 1, team: 2, viewer: 3, editor: 3, client: 4 };
    Object.values(childrenByMgr).forEach(arr => arr.sort((a, b) => {
      const ra = order[a.role] || 9, rb = order[b.role] || 9;
      if (ra !== rb) return ra - rb;
      return (a.name || '').localeCompare(b.name || '');
    }));

    const out = [];
    const walk = (member, depth) => {
      out.push({ member, depth });
      const kids = childrenByMgr[member.member_id] || [];
      for (const k of kids) walk(k, depth + 1);
    };
    if (owner) walk(owner, 0);
    return { rows: out, unassigned: childrenByMgr['__none__'] || [] };
  }, [data.members]);

  const candidatesForRole = useMemo(() => {
    const allowed = data.allowed_managers_by_role || {};
    // 2026-05-25 — sort candidates by role tier (owner → admin → manager) then
    // by name, so the per-row picker reads top-down per Owner spec.
    const tierOrder = { owner: 0, admin: 1, manager: 2 };
    const out = {};
    for (const role of ['admin', 'manager', 'team']) {
      const allowedRoles = allowed[role] || [];
      out[role] = data.members
        .filter(m => allowedRoles.includes(m.role))
        .sort((a, b) => (tierOrder[a.role] ?? 9) - (tierOrder[b.role] ?? 9) || (a.name || '').localeCompare(b.name || ''));
    }
    return out;
  }, [data.members, data.allowed_managers_by_role]);

  if (loading) {
    return (
      <Card className="bg-card/60 backdrop-blur" data-testid="org-chart-card">
        <CardHeader className="pb-3">
          <CardTitle className="text-sm font-semibold flex items-center gap-2">
            <Network className="h-4 w-4 text-gold" /> Org Chart
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-[12px] text-muted-foreground/50">Loading...</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="bg-card/60 backdrop-blur" data-testid="org-chart-card">
      <CardHeader className="pb-3">
        <CardTitle className="text-sm font-semibold flex items-center gap-2">
          <Network className="h-4 w-4 text-gold" /> Org Chart
          {!canEdit && (
            <Badge variant="outline" className="text-[9px] border-border/30 ml-2">
              <Lock className="h-2.5 w-2.5 mr-1" /> Read-only
            </Badge>
          )}
        </CardTitle>
        <p className="text-[11px] text-muted-foreground/50 mt-1">
          Defines who reports to whom. Tasks and Calendar visibility follow this chart.
        </p>
      </CardHeader>
      <CardContent className="space-y-4">
        {!data.hierarchy_complete && (
          <div
            data-testid="org-chart-incomplete-banner"
            className="flex items-start gap-2 p-3 rounded-lg bg-amber-500/5 border border-amber-500/20 text-[12px]"
          >
            <AlertTriangle className="h-4 w-4 text-amber-400 shrink-0 mt-0.5" />
            <div>
              <p className="text-amber-300 font-medium">Hierarchy incomplete</p>
              <p className="text-muted-foreground/60 mt-0.5">
                Some members have no manager assigned. Tasks/Calendar fall back to the previous
                visibility rules (Manager/Team see only own tasks; Admin sees all) until every
                non-Owner member is assigned a manager below.
              </p>
            </div>
          </div>
        )}
        {data.hierarchy_complete && (
          <div className="flex items-center gap-2 p-2 rounded-lg bg-emerald-500/5 border border-emerald-500/15 text-[11px] text-emerald-300/80">
            <CheckCircle2 className="h-3.5 w-3.5" />
            Hierarchy fully wired - visibility rules are live.
          </div>
        )}

        <div className="space-y-1" data-testid="org-chart-tree">
          {flatTree.rows.map(({ member, depth }) => (
            <OrgRow
              key={member.member_id}
              member={member}
              depth={depth}
              canEdit={canEdit}
              candidatesForRole={candidatesForRole}
              onChange={updateManager}
              saving={savingId === member.member_id}
              isOwner={member.role === 'owner'}
            />
          ))}
        </div>

        {flatTree.unassigned.length > 0 && (
          <div className="space-y-1 pt-2 border-t border-border/20" data-testid="org-chart-unassigned">
            <p className="text-[11px] uppercase tracking-wider text-muted-foreground/40 mb-2">
              Unassigned - no manager set
            </p>
            {flatTree.unassigned.map(m => (
              <OrgRow
                key={m.member_id}
                member={m}
                depth={0}
                canEdit={canEdit}
                candidatesForRole={candidatesForRole}
                onChange={updateManager}
                saving={savingId === m.member_id}
                unassigned
              />
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function OrgRow({ member, depth, canEdit, candidatesForRole, onChange, saving, isOwner, unassigned }) {
  const indent = depth * 24;
  const candidates = candidatesForRole[member.role] || [];
  const NONE = '__none__';
  const opts = [
    { value: NONE, label: 'Not set' },
    ...candidates.map(c => ({ value: c.member_id, label: c.name + ' (' + c.role + ')' })),
  ];
  const editable = canEdit && !isOwner;
  let reportsToText = 'Reports to: Not set';
  if (member.manager_id) {
    const mgr = candidates.find(c => c.member_id === member.manager_id);
    reportsToText = 'Reports to: ' + (mgr ? mgr.name : 'Unknown');
  }
  return (
    <div
      className="flex items-center gap-3 py-2 px-2 rounded-md hover:bg-foreground/[0.02]"
      style={{ paddingLeft: indent + 8 }}
      data-testid={'org-row-' + member.role}
    >
      {depth > 0 && <ChevronRight className="h-3 w-3 text-muted-foreground/30 shrink-0" />}
      <div className="h-7 w-7 rounded-full bg-gold/10 flex items-center justify-center shrink-0">
        <span className="text-[10px] font-semibold text-gold">{(member.name || '?').charAt(0).toUpperCase()}</span>
      </div>
      <div className="flex-1 min-w-0">
        {/* Iter 65a — UserChip rendering site #3 (Org chart) */}
        <UserChip user={{ name: member.name || member.email, role: member.role }} showRole size="md" testId={`org-chip-${member.member_id}`} className="font-medium" />
        <p className="text-[10px] text-muted-foreground/40 truncate">{member.email}</p>
      </div>
      {isOwner ? (
        <span className="text-[10px] text-muted-foreground/40 italic">No manager</span>
      ) : (
        <div className="w-[220px] shrink-0">
          {editable ? (
            <StellarSelect
              value={member.manager_id || NONE}
              onValueChange={(v) => onChange(member.member_id, v === NONE ? null : v)}
              options={opts}
              placeholder="Reports to..."
              disabled={saving}
              testId={'reports-to-' + member.member_id}
              className="h-8"
            />
          ) : (
            <span className="text-[11px] text-muted-foreground/50">{reportsToText}</span>
          )}
        </div>
      )}
      {unassigned && !isOwner && (
        <Badge className="text-[9px] bg-amber-500/10 text-amber-400 border-amber-500/20">Unassigned</Badge>
      )}
    </div>
  );
}
